import { useFetchProductsQuery } from '@/features/products/list/api';
import { productsQueryBuilder } from '@/features/products/list/util';
import { Product } from '@/features/products/shared';
import React, { useEffect, useState } from 'react';
import { Link, Image, PentagonIcon, Icon, ArrowLeftIcon } from '@/components';
import { checkTime, createHandle, toMoney } from '@/libs/app';
import {
  AuctionStatus,
  DiscountStatus,
  PagePath,
  PriceSymbol,
  ProductStatus,
} from '@/layouts/shared';
import { useTranslation } from 'next-i18next';

type SearchResultsProps = {
  keyword: string;
  isShow: boolean;
  onSeeMore: Function;
};

const SearchResults = (props: SearchResultsProps) => {
  const { keyword, isShow, onSeeMore } = props;

  const { t } = useTranslation('layout');

  const [results, setResults] = useState<Product[]>([]);

  const { refetch, isLoading } = useFetchProductsQuery(
    {
      limit: 5,
      offset: 0,
      product_keyword: keyword,
    },
    {
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
      onError: ({ message }) => {},
      onSuccess: ({ data }) => {
        setResults(Array.isArray(data) ? data : []);
      },
    }
  );

  useEffect(() => {
    if (!keyword) return;

    const delayDebounce = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [keyword, refetch]);

  if (isLoading && !results.length) return null;

  return (
    <div
      className={`tw-border tw-rounded-[5px] tw-bg-white ${
        isShow && keyword ? 'tw-block' : 'tw-hidden'
      }`}
    >
      {results.length ? (
        <div>
          {results.map((p: Product) => {
            let handle = `${createHandle(p.product_name)}-${p.id}-${
              p.product_code
            }`;

            let isDiscount = p.product_discount
              ? checkTime(
                  p.product_discount?.started_at,
                  p.product_discount?.ended_at
                )
              : false;

            if (
              p.product_discount &&
              p.product_discount?.status === DiscountStatus.Ended
            )
              isDiscount = false;

            const isDeposited =
              p.quantity <= 0 ||
              p.status == ProductStatus.CloseASale ||
              p.status === ProductStatus.Deposited;

            return (
              <div
                key={`product-${p.id}`}
                className="tw-flex tw-gap-2 tw-p-2 tw-border-b"
              >
                <div className="tw-w-16 tw-h-16 tw-relative">
                  <Image
                    className="tw-w-16 tw-h-16"
                    alt={p.product_name}
                    src={
                      p.images && p.images[0]
                        ? p.images[0].url
                        : '/bg-default.png'
                    }
                  ></Image>
                  {p.product_discount && isDiscount && (
                    <>
                      <div className="tw-w-5 tw-h-5 tw-absolute tw-top-1 tw-right-1 tw-z-10">
                        <Image
                          src="/sale.png"
                          className="tw-w-full tw-h-full"
                          alt="sale"
                        ></Image>
                      </div>
                    </>
                  )}
                  {p.is_auction && (
                    <>
                      <div className="tw-w-5 tw-h-5 tw-absolute tw-top-1 tw-right-1 tw-z-10">
                        <Image
                          src="/auction.png"
                          className="tw-w-full tw-h-full"
                          alt="auction"
                        ></Image>
                      </div>
                    </>
                  )}
                  {isDeposited && (
                    <div className="tw-absolute tw-top-0 tw-left-0 tw-z-10 tw-scale-50 -tw-translate-x-4 -tw-translate-y-1">
                      <PentagonIcon text="Đã bán" />{' '}
                    </div>
                  )}
                </div>
                <div className="tw-text-sm">
                  <Link
                    url={{
                      pathname: PagePath.ProductDetailPage,
                      query: { id: handle },
                    }}
                    className="tw-font-bold tw-text-primary-main text-2line"
                  >
                    {p.product_name}
                  </Link>
                  {p.quotation ? (
                    <span className="tw-text-secondary-70">Liên hệ</span>
                  ) : (
                    <>
                      {p.is_auction && p.auction ? (
                        <>
                          <span className="tw-text-md tw-block tw-text-secondary-70 tw-font-semibold">
                            {toMoney(
                              p.auction.status === AuctionStatus.Pending
                                ? p.auction.origin_price || 0
                                : p.auction.sell_price || 0,
                              t,
                              PriceSymbol.$
                            )}
                          </span>
                        </>
                      ) : (
                        <>
                          {p.product_discount && isDiscount ? (
                            <>
                              <span className="tw-text-md tw-block tw-text-secondary-70 tw-font-semibold">
                                {toMoney(
                                  p.price -
                                    (p.price * p.product_discount.discount) /
                                      100,
                                  t,
                                  PriceSymbol.$
                                )}
                              </span>
                              <span className="tw-block tw-text-secondary-81 tw-font-light tw-text-sm tw-line-through tw-italic">
                                {toMoney(p.price, t, PriceSymbol.$)}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="tw-text-md tw-block tw-text-secondary-70 tw-font-semibold">
                                {toMoney(p.price, t, PriceSymbol.$)}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          <div className="tw-p-2 tw-flex tw-items-center tw-justify-center">
            <div
              className="tw-flex tw-items-center tw-cursor-pointer tw-text-secondary-70 hover:tw-text-primary-main"
              onClick={() => onSeeMore()}
            >
              <span className="tw-text-sm tw-font-semibold">Xem thêm</span>
              <Icon
                className="tw-rotate-180 tw-scale-75"
                icon={<ArrowLeftIcon />}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="tw-p-2 tw-text-center tw-border-b">
          Không tìm thấy sản phẩm
        </div>
      )}
    </div>
  );
};

export default SearchResults;
